/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/layout/Layout';
import { useNavigate, useLocation } from 'react-router-dom';
import * as shaApi from '../services/sha.api';
import { fmtDateTime, fmtCurrency } from '../utils/helpers';
import PatientSearchInput from '../components/patient/PatientSearchInput';
import {getCurrencySymbol} from '../utils/locale';
import {useTranslation} from 'react-i18next';

const INP = 'w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#039089] transition-all placeholder:text-slate-400';
const LBL = 'block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5';
const BTN = 'bg-[#039089] hover:bg-teal-700 text-white font-semibold py-2.5 px-5 rounded-xl transition-all text-sm shadow-md disabled:opacity-50 disabled:cursor-not-allowed';
const BTN2 = 'bg-white hover:bg-slate-50 text-slate-700 font-semibold py-2.5 px-5 rounded-xl border-2 border-slate-200 hover:border-slate-300 transition-all text-sm';

const STATUS_CSS: Record<string,string> = {
  PENDING:'bg-amber-100 text-amber-700',
  PENDING_DOCTOR_APPROVAL:'bg-red-100 text-red-700',
  FINALISED:'bg-emerald-100 text-emerald-700 font-bold',
  REJECTED:'bg-red-200 text-red-800 font-bold',
  CANCELLED:'bg-slate-100 text-slate-500',
};

export default function SHAPreAuthPage() {
  const {t}=useTranslation();
  const nav = useNavigate();
  const loc = useLocation();
  const [preauths, setPreauths] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [shaClaimId, setShaClaimId] = useState('');
  const [pollingId, setPollingId] = useState<string|null>(null);

  // Patient-driven auto-load
  const [selPatient, setSelPatient] = useState<any>(null);
  const [patientClaims, setPatientClaims] = useState<any[]>([]);
  const [patientOrders, setPatientOrders] = useState<any[]>([]);
  const [patientDataLoading, setPatientDataLoading] = useState(false);

  // Create preauth form
  const [showForm, setShowForm] = useState(false);
  const [preauthType, setPreauthType] = useState<'NORMAL'|'SURGICAL'|'RENAL'|'ONCOLOGY'|'IMAGING'|'OPTICAL'>('NORMAL');
  const [form, setForm] = useState<any>({
    shaClaimId:'', consentToken:'', interventionCode:'',
    requestedAmount:'', diagnosis:'', doctorName:'', doctorRegNo:'',
    clinicalIndications:'',
    // surgical — field names match DHA's exact preauth API fields
    chiefComplaint:'', vitalSigns:'', historyOfPresentIllness:'', physicalExamination:'',
    investigationReportDetails:'', typeOfAnaesthesia:'', surgeryDate:'',
    // renal
    renalSessionsRequired:'', renalCostPerSession:'', frequencyOfSessions:'',
    renalClinicalIndications:'', renalStartDate:'', renalIsCoInsured:false,
    // oncology
    carcinomaStaging:'', comorbidity:'', metastases:'', treatmentSetting:'',
    oncologySessionsRequired:'', oncologyCostPerSession:'', oncologyIsCoInsured:false,
    // imaging
    imagingClinicalIndications:'',
    // optical
    necessityOfService:'', lensPrescription:'', lensAmount:'', eyeExaminationAmount:'',
    frameAmount:'', newOrReplacement:'',
    // doctor consent
    doctorIdNumber:'', doctorConsentRequestType:'PREAUTH_DOCTOR_APPROVAL_REQUEST',
  });

  const notify = (m:string) => { setMsg(m); setTimeout(()=>setMsg(''),5000); };

  const loadPreauths = useCallback(async () => {
    if (!shaClaimId.trim()) return;
    setLoading(true);
    try {
      const r = await shaApi.shaGetPreauthsByClaim(shaClaimId.trim());
      setPreauths(r?.data||[]);
    } catch { setPreauths([]); }
    setLoading(false);
  }, [shaClaimId]);

  // When a patient is selected, pull their existing SHA claims + any CPOE orders
  // still waiting on pre-auth, so the clerk isn't typing claim UUIDs / intervention
  // codes / amounts from memory. Doesn't replace the manual "paste a claim ID" box
  // (kept for claims that predate/bypass HMIS patient linkage) — it's additive.
  const loadPatientData = useCallback(async (patient: any) => {
    setSelPatient(patient);
    setPatientClaims([]); setPatientOrders([]);
    if (!patient?.id) return;
    setPatientDataLoading(true);
    try {
      const [claimsRes, ordersRes] = await Promise.all([
        shaApi.shaGetClaimsByPatient(patient.id).catch(() => ({ data: [] })),
        shaApi.shaGetPendingPreauthOrders(patient.id).catch(() => ({ data: [] })),
      ]);
      const claims = claimsRes?.data || [];
      setPatientClaims(claims);
      setPatientOrders(ordersRes?.data || []);
      // Exactly one usable (non-closed) claim? Auto-select it so "Load Preauths" works immediately.
      const active = claims.filter((c: any) => !['CLOSED'].includes(c.status));
      if (active.length === 1) setShaClaimId(active[0].id);
    } finally {
      setPatientDataLoading(false);
    }
  }, []);

  const clearPatientData = () => {
    setSelPatient(null); setPatientClaims([]); setPatientOrders([]);
  };

  // Deep-link from Front Office Billing's "🔒 Pre-Auth Pending · Review →" badge — front-desk
  // could see a preauth was pending but had no direct way to jump into reviewing/submitting
  // it for that specific patient; this page had no route-state handling at all before.
  useEffect(() => {
    const p = (loc.state as any)?.patient;
    if (p?.id) loadPatientData(p);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.state]);

  // Prefill the New Preauth modal from a specific claim + (optionally) a CPOE order
  // that's actually awaiting pre-auth — intervention_code and requested_amount come
  // straight from Charge Master / CPOE instead of being retyped.
  // (Named applyPatientClaim/applyOrderForPreauth, not "use...", so eslint's
  // react-hooks/rules-of-hooks doesn't mistake these for custom hooks.)
  const applyPatientClaim = (claim: any) => {
    setForm((f: any) => ({
      ...f,
      shaClaimId: claim.id,
      consentToken: claim.shaConsentToken || f.consentToken,
    }));
    setShaClaimId(claim.id);
  };

  const applyOrderForPreauth = (order: any, claim?: any) => {
    setForm((f: any) => ({
      ...f,
      shaClaimId: claim?.id || f.shaClaimId,
      consentToken: claim?.shaConsentToken || f.consentToken,
      interventionCode: order.shaInterventionCode || order.sha_intervention_code || f.interventionCode,
      requestedAmount: String(order.shaApprovedRate || order.sha_approved_rate || order.estimatedAmount || order.estimated_amount || f.requestedAmount || ''),
    }));
    if (claim?.id) setShaClaimId(claim.id);
    setShowForm(true);
  };

  const pollPreauth = async (p: any) => {
    setPollingId(p.id);
    try {
      const r = await shaApi.shaPollPreauth(p.shaConsentToken, shaClaimId);
      notify(`Status: ${r?.data?.status||'—'}`);
      await loadPreauths();
    } catch(e:any){ notify('❌ '+(e?.response?.data?.message||'Poll failed')); }
    setPollingId(null);
  };

  const sendDoctorConsent = async (p: any) => {
    if (!form.doctorIdNumber) return notify('Enter doctor ID number first');
    setSaving(true);
    try {
      await shaApi.shaSendDoctorConsent({ identificationNumber:form.doctorIdNumber, requestType:'PREAUTH_DOCTOR_APPROVAL_REQUEST', consentToken:p.shaConsentToken, interventionCode:p.interventionCode });
      notify('✅ Doctor consent request sent');
    } catch(e:any){ notify('❌ '+(e?.response?.data?.message||'Failed')); }
    setSaving(false);
  };

  const cancelPreauth = async (p: any) => {
    if (!window.confirm('Cancel this preauth?')) return;
    setSaving(true);
    try {
      await shaApi.shaCancelPreauth(p.shaConsentToken, p.interventionCode, shaClaimId);
      notify('✅ Preauth cancelled'); await loadPreauths();
    } catch(e:any){ notify('❌ '+(e?.response?.data?.message||'Failed')); }
    setSaving(false);
  };

  const createPreauth = async () => {
    if (!form.shaClaimId||!form.consentToken||!form.interventionCode) return notify('Fill all required fields');
    setSaving(true);
    try {
      // Field names below must match sha-preauth.service.ts's DHA multipart form
      // fields exactly (chief_complaint, clinical_indications, etc.) — the backend
      // silently drops anything that doesn't match one of these exact keys.
      const specialData: any = {};
      if (preauthType==='SURGICAL') specialData.surgical = {
        chief_complaint:form.chiefComplaint, vital_signs:form.vitalSigns,
        history_of_present_illness:form.historyOfPresentIllness, physical_examination:form.physicalExamination,
        investigation_report_details:form.investigationReportDetails, type_of_anaesthesia:form.typeOfAnaesthesia,
        surgery_date:form.surgeryDate,
      };
      else if (preauthType==='RENAL') specialData.renal = {
        number_of_sessions_required: Number(form.renalSessionsRequired)||undefined,
        cost_per_session: Number(form.renalCostPerSession)||undefined,
        frequency_of_sessions:form.frequencyOfSessions, clinical_indications:form.renalClinicalIndications,
        start_date:form.renalStartDate, is_co_insured:form.renalIsCoInsured,
      };
      else if (preauthType==='ONCOLOGY') specialData.oncology = {
        carcinoma_staging:form.carcinomaStaging, comorbidity:form.comorbidity,
        metastases:form.metastases?form.metastases.split(',').map((s:string)=>s.trim()).filter(Boolean):undefined,
        treatment_setting:form.treatmentSetting?form.treatmentSetting.split(',').map((s:string)=>s.trim()).filter(Boolean):undefined,
        number_of_sessions_required: Number(form.oncologySessionsRequired)||undefined,
        cost_per_session: Number(form.oncologyCostPerSession)||undefined,
        is_co_insured:form.oncologyIsCoInsured,
      };
      else if (preauthType==='IMAGING') specialData.imaging = { clinical_indications:form.imagingClinicalIndications };
      else if (preauthType==='OPTICAL') specialData.optical = {
        necessity_of_service:form.necessityOfService, lens_prescription:form.lensPrescription,
        lens_amount:+form.lensAmount||undefined, eye_examination_amount:+form.eyeExaminationAmount||undefined,
        frame_amount:+form.frameAmount||undefined, new_or_replacement:form.newOrReplacement,
      };

      const indications = (
        form.clinicalIndications || form.imagingClinicalIndications || form.renalClinicalIndications || form.chiefComplaint || form.necessityOfService || ''
      ).trim() || `Clinical indication for ${form.interventionCode} (ICD-11 ${form.diagnosis})`;

      await shaApi.shaCreatePreauth({
        shaClaimId:form.shaClaimId, consentToken:form.consentToken, interventionCode:form.interventionCode,
        requestedAmount:+form.requestedAmount, diagnosis:form.diagnosis,
        clinicalIndications: indications, clinical_indications: indications,
        doctorDetails:{ name:form.doctorName, registrationNumber:form.doctorRegNo },
        isSurgical:preauthType==='SURGICAL', isRenal:preauthType==='RENAL',
        isOncology:preauthType==='ONCOLOGY', isImaging:preauthType==='IMAGING',
        isOptical:preauthType==='OPTICAL', needsManualApproval:true,
        specialData,
      });
      notify('✅ Preauth created'); setShowForm(false);
      setShaClaimId(form.shaClaimId); await loadPreauths();
    } catch(e:any){ notify('❌ '+(e?.response?.data?.message||'Failed')); }
    setSaving(false);
  };

  return (
    <Layout title="SHA Preauthorizations" actions={
      <div className="flex gap-2">
        <button onClick={()=>setShowForm(true)} className={BTN+' text-xs'}>➕ New Preauth</button>
        <button onClick={()=>nav('/sha')} className={BTN2+' text-xs'}>← SHA Dashboard</button>
      </div>
    }>
      {msg && <div className={`fixed top-5 right-5 z-[99999] px-5 py-3 rounded-2xl shadow-xl text-sm font-semibold border ${msg.startsWith('✅')?'bg-emerald-50 text-emerald-800 border-emerald-200':'bg-red-50 text-red-800 border-red-200'}`}>{msg}</div>}

      {/* Find by Patient — auto-loads claims + orders awaiting pre-auth */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 mb-5">
        <p className={LBL}>Find by Patient</p>
        <PatientSearchInput selected={selPatient} onSelect={loadPatientData} onClear={clearPatientData} placeholder="Search patient by name, UHID or phone..."/>

        {patientDataLoading && <p className="text-xs text-slate-400 mt-3">Loading claims & pending orders…</p>}

        {selPatient && !patientDataLoading && (
          <div className="mt-4 space-y-3">
            {/* Existing SHA claims for this patient */}
            <div>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">SHA Claims</p>
              {patientClaims.length===0
                ? <p className="text-xs text-slate-400">No SHA claim found for this patient yet — create one from the SHA Claims page first.</p>
                : (
                  <div className="space-y-1.5">
                    {patientClaims.map((c:any)=>(
                      <div key={c.id} className="flex items-center justify-between gap-3 bg-slate-50 rounded-xl px-3 py-2">
                        <div className="min-w-0">
                          <p className="text-xs font-mono text-slate-600 truncate">{c.id}</p>
                          <p className="text-xs text-slate-400">{c.status} · {c.interventionCode||'—'}</p>
                        </div>
                        <button onClick={()=>applyPatientClaim(c)} className={BTN2+' text-xs !py-1.5 !px-3 flex-shrink-0'}>Use This Claim</button>
                      </div>
                    ))}
                  </div>
                )}
            </div>

            {/* CPOE orders flagged requires_pre_auth, still awaiting authorization */}
            <div>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Orders Awaiting Pre-Auth</p>
              {patientOrders.length===0
                ? <p className="text-xs text-slate-400">No orders currently require pre-authorization for this patient.</p>
                : (
                  <div className="space-y-1.5">
                    {patientOrders.map((o:any)=>(
                      <div key={o.id} className={`flex items-center justify-between gap-3 rounded-xl px-3 py-2 border ${o.preauth_approved?'bg-emerald-50 border-emerald-200':'bg-red-50 border-red-200'}`}>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-700 truncate">{o.item_name}</p>
                          <p className="text-xs text-slate-400">{o.order_type} · {o.sha_intervention_code||'no intervention code'} · {fmtCurrency(o.sha_approved_rate||o.estimated_amount||0)}</p>
                        </div>
                        {o.preauth_approved
                          ? <span className="text-xs font-bold text-emerald-700 flex-shrink-0">✅ Approved</span>
                          : <button onClick={()=>applyOrderForPreauth(o, patientClaims.find((c:any)=>!['CLOSED'].includes(c.status)))} className={BTN+' text-xs !py-1.5 !px-3 flex-shrink-0'}>Request Pre-Auth</button>}
                      </div>
                    ))}
                  </div>
                )}
            </div>
          </div>
        )}
      </div>

      {/* Manual lookup — for claims that predate/bypass HMIS patient linkage */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 mb-5">
        <div className="flex gap-3 items-end">
          <div className="flex-1"><label className={LBL}>SHA Claim ID</label>
            <input value={shaClaimId} onChange={e=>setShaClaimId(e.target.value)} className={INP} placeholder="Paste SHA Claim UUID" onKeyDown={e=>e.key==='Enter'&&loadPreauths()}/></div>
          <button onClick={loadPreauths} disabled={loading||!shaClaimId.trim()} className={BTN}>🔍 Load Preauths</button>
        </div>
      </div>

      {/* Doctor Consent Quick Action */}
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-5 flex items-center gap-4">
        <div className="text-2xl">👨‍⚕️</div>
        <div className="flex-1">
          <p className="font-bold text-amber-800 text-sm">Doctor Consent Pending?</p>
          <p className="text-xs text-amber-600">Enter the doctor's ID number here to resend the consent request.</p>
        </div>
        <input value={form.doctorIdNumber} onChange={e=>setForm((f:any)=>({...f,doctorIdNumber:e.target.value}))} className={INP+' max-w-[180px]'} placeholder="Doctor ID No."/>
      </div>

      {/* Preauth List */}
      {loading ? <div className="p-12 text-center"><div className="w-8 h-8 border-4 border-[#039089]/20 border-t-[#039089] rounded-full animate-spin mx-auto"/></div>
      : preauths.length===0 ? <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-12 text-center text-slate-400"><p className="text-5xl mb-3">🔒</p><p>Enter a SHA Claim ID to view preauths</p></div>
      : (
        <div className="space-y-3">
          {preauths.map((p: any) => (
            <div key={p.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
              <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                  <p className="font-bold text-slate-800">{p.interventionCode}</p>
                  <p className="text-xs text-slate-400 font-mono mt-0.5">Token: {p.shaConsentToken?.slice(0,20)}...</p>
                </div>
                <span className={`text-sm px-3 py-1 rounded-full font-bold flex-shrink-0 ${STATUS_CSS[p.status]||'bg-slate-100 text-slate-500'}`}>{p.status}</span>
              </div>

              <div className="grid grid-cols-3 gap-3 mb-4">
                {[
                  ['Requested',`${getCurrencySymbol()} ${p.requestedAmount||'—'}`],
                  ['Approved',`${getCurrencySymbol()} ${p.approvedAmount||'—'}`],
                  ['Finalised',p.finalisedAt?fmtDateTime(p.finalisedAt):'—'],
                ].map(([l,v])=>(
                  <div key={String(l)} className="bg-slate-50 rounded-xl p-2.5">
                    <p className="text-xs text-slate-400 font-bold uppercase">{l}</p>
                    <p className="text-sm font-semibold text-slate-700">{v}</p>
                  </div>
                ))}
              </div>

              {p.rejectionReason && <div className="bg-red-50 rounded-xl p-3 mb-3 text-xs text-red-700">Rejection: {p.rejectionReason}</div>}

              {/* Type badges */}
              <div className="flex flex-wrap gap-1.5 mb-4">
                {p.isSurgical&&<span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-bold">Surgical</span>}
                {p.isRenal&&<span className="text-xs bg-blue-100 text-[#02357A] px-2 py-0.5 rounded-full font-bold">Renal</span>}
                {p.isOncology&&<span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-bold">Oncology</span>}
                {p.isImaging&&<span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-bold">Imaging</span>}
                {p.isOptical&&<span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full font-bold">Optical</span>}
                {p.needsManualApproval&&<span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-bold">⚠️ Manual Approval Required</span>}
              </div>

              <div className="flex flex-wrap gap-2">
                {['PENDING','PENDING_DOCTOR_APPROVAL'].includes(p.status)&&(
                  <button onClick={()=>pollPreauth(p)} disabled={pollingId===p.id} className={BTN+' text-xs !py-2 !px-3'}>
                    {pollingId===p.id?<><span className="animate-spin inline-block mr-1">⟳</span>Polling...</>:'🔄 Poll Status'}
                  </button>
                )}
                {p.status==='PENDING_DOCTOR_APPROVAL'&&(
                  <button onClick={()=>sendDoctorConsent(p)} disabled={saving} className={BTN2+' text-xs !py-2 !px-3'}>👨‍⚕️ Resend Doctor Consent</button>
                )}
                {!['FINALISED','REJECTED','CANCELLED'].includes(p.status)&&(
                  <button onClick={()=>cancelPreauth(p)} disabled={saving} className="bg-red-50 hover:bg-red-100 text-red-700 font-semibold py-2 px-3 rounded-xl border border-red-200 text-xs transition-all">🚫 Cancel</button>
                )}
                {p.status==='FINALISED'&&(
                  <button onClick={()=>nav(`/sha/claims/${shaClaimId}`)} className={BTN+' text-xs !py-2 !px-3'}>📋 Manage Claim →</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Preauth Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[9999]" onClick={()=>setShowForm(false)}>
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto p-5" onClick={e=>e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg">New Preauthorization</h3>
              <button onClick={()=>setShowForm(false)} className="text-slate-400 hover:text-slate-600 text-xl">✕</button>
            </div>

            {/* Preauth type selector */}
            <div className="flex gap-2 flex-wrap mb-4">
              {(['NORMAL','SURGICAL','RENAL','ONCOLOGY','IMAGING','OPTICAL'] as const).map(t=>(
                <button key={t} onClick={()=>setPreauthType(t)} className={`text-xs px-3 py-1.5 rounded-xl font-bold border-2 transition-all ${preauthType===t?'bg-[#039089] text-white border-[#039089]':'border-slate-200 text-slate-600 hover:border-[#039089]'}`}>{t}</button>
              ))}
            </div>

            <div className="space-y-3">
              {[['shaClaimId','SHA Claim ID *'],['consentToken','Consent Token *'],['interventionCode','Intervention Code *'],['requestedAmount','Requested Amount *'],['diagnosis','Diagnosis *'],['clinicalIndications','Clinical Indications *'],['doctorName','Doctor Name'],['doctorRegNo','Doctor Reg. Number']].map(([n,l])=>(
                <div key={n}><label className={LBL}>{l}</label><input value={form[n]} onChange={e=>setForm((f:any)=>({...f,[n]:e.target.value}))} className={INP}/></div>
              ))}

              {preauthType==='SURGICAL' && (
                <div className="grid grid-cols-2 gap-3">
                  {[['chiefComplaint','Chief Complaint'],['vitalSigns','Vital Signs'],['historyOfPresentIllness','History of Present Illness'],['physicalExamination','Physical Examination'],['investigationReportDetails','Investigation Report Details'],['typeOfAnaesthesia','Type of Anaesthesia']].map(([n,l])=>(
                    <div key={n}><label className={LBL}>{l}</label><input value={form[n]} onChange={e=>setForm((f:any)=>({...f,[n]:e.target.value}))} className={INP}/></div>
                  ))}
                  <div><label className={LBL}>Surgery Date</label><input type="datetime-local" value={form.surgeryDate} onChange={e=>setForm((f:any)=>({...f,surgeryDate:e.target.value}))} className={INP}/></div>
                </div>
              )}
              {preauthType==='RENAL' && (
                <div className="grid grid-cols-2 gap-3">
                  <div><label className={LBL}>No. of Sessions Required</label><input type="number" value={form.renalSessionsRequired} onChange={e=>setForm((f:any)=>({...f,renalSessionsRequired:e.target.value}))} className={INP}/></div>
                  <div><label className={LBL}>Cost Per Session ({getCurrencySymbol()})</label><input type="number" value={form.renalCostPerSession} onChange={e=>setForm((f:any)=>({...f,renalCostPerSession:e.target.value}))} className={INP}/></div>
                  <div><label className={LBL}>Frequency of Sessions</label><input value={form.frequencyOfSessions} onChange={e=>setForm((f:any)=>({...f,frequencyOfSessions:e.target.value}))} className={INP} placeholder="e.g. 3x/week"/></div>
                  <div><label className={LBL}>Start Date</label><input type="date" value={form.renalStartDate} onChange={e=>setForm((f:any)=>({...f,renalStartDate:e.target.value}))} className={INP}/></div>
                  <div className="col-span-2"><label className={LBL}>Clinical Indications</label><input value={form.renalClinicalIndications} onChange={e=>setForm((f:any)=>({...f,renalClinicalIndications:e.target.value}))} className={INP}/></div>
                  <div className="col-span-2 flex items-center gap-2"><input type="checkbox" checked={form.renalIsCoInsured} onChange={e=>setForm((f:any)=>({...f,renalIsCoInsured:e.target.checked}))} className="w-4 h-4 accent-[#039089]"/><label className="text-sm text-slate-700">Co-insured</label></div>
                </div>
              )}
              {preauthType==='ONCOLOGY' && (
                <div className="grid grid-cols-2 gap-3">
                  <div><label className={LBL}>Carcinoma Staging</label><input value={form.carcinomaStaging} onChange={e=>setForm((f:any)=>({...f,carcinomaStaging:e.target.value}))} className={INP}/></div>
                  <div><label className={LBL}>Comorbidity</label><input value={form.comorbidity} onChange={e=>setForm((f:any)=>({...f,comorbidity:e.target.value}))} className={INP}/></div>
                  <div><label className={LBL}>Metastases</label><input value={form.metastases} onChange={e=>setForm((f:any)=>({...f,metastases:e.target.value}))} className={INP} placeholder="comma-separated"/></div>
                  <div><label className={LBL}>Treatment Setting</label><input value={form.treatmentSetting} onChange={e=>setForm((f:any)=>({...f,treatmentSetting:e.target.value}))} className={INP} placeholder="comma-separated"/></div>
                  <div><label className={LBL}>No. of Sessions Required</label><input type="number" value={form.oncologySessionsRequired} onChange={e=>setForm((f:any)=>({...f,oncologySessionsRequired:e.target.value}))} className={INP}/></div>
                  <div><label className={LBL}>Cost Per Session ({getCurrencySymbol()})</label><input type="number" value={form.oncologyCostPerSession} onChange={e=>setForm((f:any)=>({...f,oncologyCostPerSession:e.target.value}))} className={INP}/></div>
                  <div className="col-span-2 flex items-center gap-2"><input type="checkbox" checked={form.oncologyIsCoInsured} onChange={e=>setForm((f:any)=>({...f,oncologyIsCoInsured:e.target.checked}))} className="w-4 h-4 accent-[#039089]"/><label className="text-sm text-slate-700">Co-insured</label></div>
                </div>
              )}
              {preauthType==='IMAGING' && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2"><label className={LBL}>Clinical Indications</label><input value={form.imagingClinicalIndications} onChange={e=>setForm((f:any)=>({...f,imagingClinicalIndications:e.target.value}))} className={INP}/></div>
                </div>
              )}
              {preauthType==='OPTICAL' && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2"><label className={LBL}>Necessity of Service</label><input value={form.necessityOfService} onChange={e=>setForm((f:any)=>({...f,necessityOfService:e.target.value}))} className={INP}/></div>
                  <div className="col-span-2"><label className={LBL}>Lens Prescription</label><input value={form.lensPrescription} onChange={e=>setForm((f:any)=>({...f,lensPrescription:e.target.value}))} className={INP}/></div>
                  <div><label className={LBL}>Lens Amount ({getCurrencySymbol()})</label><input type="number" value={form.lensAmount} onChange={e=>setForm((f:any)=>({...f,lensAmount:e.target.value}))} className={INP}/></div>
                  <div><label className={LBL}>Eye Exam Amount ({getCurrencySymbol()})</label><input type="number" value={form.eyeExaminationAmount} onChange={e=>setForm((f:any)=>({...f,eyeExaminationAmount:e.target.value}))} className={INP}/></div>
                  <div><label className={LBL}>Frame Amount ({getCurrencySymbol()})</label><input type="number" value={form.frameAmount} onChange={e=>setForm((f:any)=>({...f,frameAmount:e.target.value}))} className={INP}/></div>
                  <div><label className={LBL}>New or Replacement</label><select value={form.newOrReplacement} onChange={e=>setForm((f:any)=>({...f,newOrReplacement:e.target.value}))} className={INP}><option value="">{t('common.select','Select')}</option><option value="NEW">NEW</option><option value="REPLACEMENT">REPLACEMENT</option></select></div>
                </div>
              )}

              <div className="flex gap-3 pt-3">
                <button onClick={()=>setShowForm(false)} className={BTN2+' flex-1'}>{t('common.cancel','Cancel')}</button>
                <button onClick={createPreauth} disabled={saving||!form.shaClaimId||!form.consentToken||!form.interventionCode||!form.requestedAmount||!form.diagnosis} className={BTN+' flex-1'}>{saving?'Creating...':'Create Preauth'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
